import { useState } from "react";
import { Check, RotateCcw } from "lucide-react";
import { getDespesasDepartamentosOmie, salvarDeparaDepartamento } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import {
  AvisoErro, Badge, BarraFiltros, Card, Carregando, Seletor, Vazio, estiloTabela, formatValor, inputStyle, opcoesEmpresa,
  useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// De-para de departamentos (Fase 2): departamento do Omie de cada empresa → departamento da
// plataforma (os mesmos do orçamento). Automático pelo nome; sub-departamento herda do pai
// (GERÊNCIA FINANCEIRA → DIRETORIA ADMINISTRATIVO FINANCEIRO). O manual nunca é sobrescrito pela
// sincronização e reclassifica na hora os lançamentos já sincronizados.

const NENHUM = "__nenhum__";

function LinhaDepartamento({ C, T, linha, opcoes, onSalvo, onErro }) {
  const atual = linha.departamento || (linha.deparaManual ? NENHUM : "");
  const [valor, setValor] = useState(atual);
  const [salvando, setSalvando] = useState(false);
  const mudou = valor !== atual;
  const nivel = (linha.estrutura || "").split(".").length - 1;

  const salvar = async (dados) => {
    setSalvando(true);
    try {
      await salvarDeparaDepartamento(linha.id, dados);
      onSalvo();
    } catch (e) {
      onErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <tr style={linha.inativo ? { opacity: 0.55 } : undefined}>
      <td style={T.td}>{linha.empresa}</td>
      <td style={{ ...T.td, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{linha.estrutura || "—"}</td>
      <td style={{ ...T.td, paddingLeft: 10 + Math.max(nivel - 1, 0) * 16 }}>
        <span style={{ fontWeight: nivel <= 1 ? 600 : 400 }}>{linha.descricao}</span>
        {linha.inativo && <span style={{ color: C.muted }}> (inativo no Omie)</span>}
      </td>
      <td style={{ ...T.td, ...T.tdNum }}>{linha.lancamentos || "—"}</td>
      <td style={{ ...T.td, ...T.tdNum }}>{formatValor(linha.valor)}</td>
      <td style={{ ...T.td, minWidth: 280 }}>
        <select value={valor} onChange={(e) => setValor(e.target.value)} style={{ ...inputStyle(C), minHeight: 32, padding: "5px 8px", fontSize: 12 }}>
          <option value="">— sem de-para —</option>
          {opcoes.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value={NENHUM}>Não é departamento (ignorar)</option>
        </select>
      </td>
      <td style={{ ...T.td, whiteSpace: "nowrap" }}>
        {mudou ? (
          <button
            onClick={() => salvar({ departamento: valor && valor !== NENHUM ? valor : null })} disabled={salvando || valor === ""}
            title={valor === "" ? "Escolha um departamento ou “Não é departamento”" : "Salvar"}
            style={{ background: C.brand, color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, opacity: valor === "" ? 0.5 : 1 }}
          >
            <Check size={13} /> {salvando ? "…" : "Salvar"}
          </button>
        ) : (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            {linha.deparaManual ? (
              <Badge cor={C.brand} title={linha.deparaPor ? `Definido por ${linha.deparaPor}` : undefined}>{linha.departamento ? "Manual" : "Ignorado"}</Badge>
            ) : linha.departamento ? (
              <Badge cor={C.success}>Automático</Badge>
            ) : (
              <Badge cor={linha.lancamentos ? C.danger : C.warning}>Pendente</Badge>
            )}
            {linha.deparaManual && (
              <button onClick={() => salvar({ automatico: true })} disabled={salvando} title="Voltar à regra automática (nome / departamento pai)" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", display: "flex", padding: 2 }}>
                <RotateCcw size={13} />
              </button>
            )}
          </span>
        )}
      </td>
    </tr>
  );
}

export default function DeparaDepartamentosRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const [empresa, setEmpresa] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [erroAcao, setErroAcao] = useState("");
  const { dados, carregando, erro, recarregar } = useCarregar(() => getDespesasDepartamentosOmie({ empresa }), [empresa]);
  const T = estiloTabela(C);

  const todos = dados?.departamentos || [];
  const pendentes = todos.filter((d) => !d.departamento && !d.deparaManual && (d.estrutura || "").includes("."));
  const linhas = filtro === "pendentes" ? pendentes : todos;

  return (
    <RealizadoLayout titulo="De-para de departamentos" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErroAcao("")}>{erro || erroAcao}</AvisoErro>
      {carregando && !dados ? <Carregando C={C} /> : dados && (
        <Card
          C={C}
          titulo="Departamento do Omie → departamento da plataforma"
          subtitulo={`${pendentes.length} departamento(s) sem de-para. Mesmo nome = automático; sub-departamento herda do pai. Ao salvar, os lançamentos já sincronizados são reclassificados.`}
          acoes={
            <BarraFiltros>
              <Seletor C={C} rotulo="Empresa" value={empresa} onChange={setEmpresa} opcoes={opcoesEmpresa} />
              <Seletor C={C} rotulo="Mostrar" value={filtro} onChange={setFiltro} opcoes={[
                { value: "todos", label: "Todos" }, { value: "pendentes", label: "Só pendentes" },
              ]} />
            </BarraFiltros>
          }
        >
          {linhas.length === 0 ? (
            <Vazio C={C}>{todos.length ? "Nenhum departamento pendente. 🎉" : "Nenhum departamento ainda — rode a sincronização em Despesas."}</Vazio>
          ) : (
            <div style={{ ...T.wrap, maxHeight: "62vh" }}>
              <table style={{ ...T.table, minWidth: 1000 }}>
                <thead><tr>
                  <th style={T.th}>Empresa</th>
                  <th style={T.th}>Estrutura</th>
                  <th style={T.th}>Departamento no Omie</th>
                  <th style={{ ...T.th, ...T.thNum }}>Lançamentos</th>
                  <th style={{ ...T.th, ...T.thNum }}>Valor</th>
                  <th style={T.th}>Departamento da plataforma</th>
                  <th style={T.th} />
                </tr></thead>
                <tbody>
                  {linhas.map((l) => (
                    <LinhaDepartamento
                      key={`${l.id}-${l.departamento}-${l.deparaManual}`} C={C} T={T} linha={l} opcoes={dados.opcoes}
                      onSalvo={recarregar} onErro={setErroAcao}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
            A linha de estrutura “001” é a própria empresa no Omie (GT, Cigta, CRIA, FLOW) e fica sem de-para.
            “Não é departamento (ignorar)” serve para ENCONTRO DE CONTAS, “Não usar” e afins: o lançamento continua aparecendo como sem departamento.
          </div>
        </Card>
      )}
    </RealizadoLayout>
  );
}
